/* eslint-disable max-len */
// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';

import { GridApi, RowDataUpdatedEvent, ICellRendererParams } from 'wdna-grid-table/lib/types';
import { WidgetGrid, JarvisWidget } from 'wdna-components/lib';
import Table, { TableButtonsConfig } from 'wdna-components/lib/table/Table';
import ContainerModal from 'wdna-components/lib/modals/generics/ContainerModal';

import { ContextType, FilterContext } from '../../../../../types';
import { CARDINALS } from './pressets';
import ChartWindRoseSingle from './ChartWindRoseSingle';
import ChartWindRose from './ChartWindRose';
import Chart from './Chart';
import ChartSimple from './ChartSimple';

type Props = unknown;

interface State {
  modalShow: boolean;
}

export default class Container extends React.Component<Props, State> {
  private mounted: boolean;
  private buttons: TableButtonsConfig;

  public static contextType: React.Context<ContextType> = FilterContext;
  public context: React.ContextType<typeof FilterContext> | undefined;

  public constructor(props: Props) {
    super(props);

    this.mounted = false;
    this.buttons = {
      search: false,
      filters: false,
      reset: false,
      export: false,
    };

    this.state = {
      modalShow: false,
    };
  }

  public componentDidMount(): void {
    this.mounted = true;
  }

  public componentWillUnmount(): void {
    this.mounted = false;
  }

  public setLocalState = (nextState: Partial<State>, callback?: (() => void)): void => {
    if (this.mounted) {
      this.setState((prevState: State) => ({
        ...prevState,
        ...nextState,
      }), callback);
    }
  };

  public rowDataUpdated = (event: RowDataUpdatedEvent): void => {
    const gridApi: GridApi = event.api;
    setTimeout((): void => {
      gridApi?.sizeColumnsToFit();
    }, 100);
  };

  public getColumnAlias = (name: string[] = []): Record<string, string> => {
    const columnAlias: Record<string, string> = {
      date: 'Date',
    };
    name.forEach((item: string, idx: number) => {
      columnAlias[`d0${idx}`] = item;
    });

    return columnAlias;
  };

  public dateCellRenderer = (params: ICellRendererParams): string => {
    let { value } = params;
    [value] = value.split(' ');
    return value;
  };

  public numberCellRenderer = (params: ICellRendererParams): string => {
    let { value } = params;
    value = !isNaN(value) ? value && value.toFixed(2) : '';
    return value;
  };

  public degtoCompass = (num: number): string => {
    const val: number = Math.floor((num / 22.5) + 0.5);
    return CARDINALS[(val % 16)];
  };

  public onModalOpen = (): void => {
    this.setLocalState({ modalShow: true });
  };

  public onModalClose = (): void => {
    this.setLocalState({ modalShow: false });
  };

  public render(): JSX.Element {
    const { modalShow } = this.state;
    const { data1, data2, data3, data4, data5, nameStation } = this.context?.parentState || {};

    // Attributes TABLE1 *************************************************
    const columnAlias1: Record<string, string> = this.getColumnAlias(data3 as string[]);
    const columnsToHide1: string[] = [
      'timestamp',
      'sensorId',
      'gatewayId',
      'chain',
      'sensorCode',
      'typeSensorId',
      'companyId',
      'companyName',
      'sensorName',
      'd05',
      'd06',
      'd07',
      'd09',
      'd10',
    ];
    const columnStyles1: Record<string, any> = {
      date: { textAlign: 'center' },
    };
    const customColumnsDefs1: Record<string, any> = {
      date: { suppressSizeToFit: false, minWidth: 150, maxWidth: 150 },
      d00: { cellRenderer: this.numberCellRenderer },
      d01: { cellRenderer: this.numberCellRenderer },
      d02: { cellRenderer: this.numberCellRenderer },
      d03: { cellRenderer: this.numberCellRenderer },
      d04: { cellRenderer: this.numberCellRenderer },
      d08: { cellRenderer: this.numberCellRenderer },
    };

    // Attributes TABLE2 *************************************************
    const columnAlias2: Record<string, any> = {
      0: 'Date',
      1: 'Count',
      4: 'W-Speed Max (km/h)',
      6: 'T Min (ºC)',
      7: 'T Max (ºC)',
      29: 'Pr (l/m2)',
      30: 'W-Dir Mean (º)',
      31: 'ET (mm)',
    };
    const columnsToHide2: string[] = [
      '1', '2', '3', '5', '8', '9', '10', '11', '12', '13', '14', '15',
      '16', '17', '18', '19', '20', '21', '22', '23', '24', '25', '26', '27', '28', '30'
    ];
    const columnStyles2: Record<string, any> = {
      0: { textAlign: 'center' },
    };
    const customColumnsDefs2: Record<string, any> = {
      0: { cellRenderer: this.dateCellRenderer },
      1: { cellRenderer: this.numberCellRenderer },
      4: { cellRenderer: this.numberCellRenderer },
      6: { cellRenderer: this.numberCellRenderer },
      7: { cellRenderer: this.numberCellRenderer },
      29: { cellRenderer: this.numberCellRenderer },
      30: { cellRenderer: this.numberCellRenderer },
      31: { cellRenderer: this.numberCellRenderer },
    };

    const dataWind: any[] = data1 || [];
    const dataAc: any[] = data2 || [];

    return (
      <div id="content">
        <WidgetGrid>
          <div className="row">
            <article className="col-sm-12 col-md-12 col-lg-12">
              <JarvisWidget
                editbutton={false}
                togglebutton={false}
                colorbutton={false}
                deletebutton={false}
                custombutton={false}
                fullscreenbutton={false}
              >
                <div className="container" style={{ paddingTop: '0px' }}>
                  <div className="row">
                    <div className="col-sm-12 col-md-6">
                      { /* Meteogram */ }
                      <Chart data={data4 || []} title={`<b>Meteogram - ${nameStation}</b>`} />
                    </div>
                    <div className="col-sm-12 col-md-2">
                      <div className="col">
                        { /* Wind speed */ }
                        <ChartSimple
                          data={[{ data: [dataWind[0] && dataWind[0].d01],
                            dataLabels: {
                              format:
                                '<div style="text-align:center; color:#FFFF">'
                                + '<span style="font-size:17px">{y:,.2f} </span>'
                                + '<span style="font-size:8px">km/h</span><br />'
                                + '</div>'
                            },
                          }]}
                          graphType="gaugeSimple"
                          title="Current Wind Speed"
                          min={0}
                          max={80}
                        />
                        <div style={{ textAlign: 'center', margin: '0px' }}>{`Max Wind Gust: ${dataAc[0] && dataAc[0][2].toFixed(2)}km/h`}</div>
                      </div>
                      <div className="col">
                        { /* Wind direction */ }
                        <ChartWindRoseSingle data={data1} title="<b>Current Wind Direction</b>" />
                        <div style={{ textAlign: 'center', margin: '0px' }}>{`Max Wind Gust Dir: ${this.degtoCompass(dataAc[0] && dataAc[0][6])}`}</div>
                      </div>
                    </div>
                    <div className="col-sm-12 col-md-2">
                      <div className="col">
                        { /* Temperature */ }
                        <ChartSimple
                          data={[{ data: [dataWind[0] && dataWind[0].d02],
                            dataLabels: {
                              format:
                                '<div style="text-align:center; color:#FFFF">'
                                + '<span style="font-size:17px">{y:,.2f} </span>'
                                + '<span style="font-size:8px">ºC</span>'
                                + '</div>'
                            }, }]}
                          graphType="gaugeSimple"
                          title="Temperature"
                          min={-10}
                          max={55}
                        />
                        <div style={{ textAlign: 'center', margin: '0px' }}>{`T.Max: ${dataAc[0] && dataAc[0][4]}ºC - T.Min: ${dataAc[0] && dataAc[0][3]}ºC`}</div>
                      </div>
                      <div className="col">
                        { /* Humidity, Radiation, UV  */ }
                        <ChartSimple
                          data={[{
                            name: 'Humidity',
                            data: [{
                              color: '#10bee5',
                              radius: '112%',
                              innerRadius: '88%',
                              y: dataWind[0] && dataWind[0].d03
                            }]
                          }, {
                            name: 'Solar Radiation',
                            data: [{
                              color: '#ffdf00',
                              radius: '87%',
                              innerRadius: '63%',
                              y: ((dataWind[0] && dataWind[0].d06) / 1500) * 100
                            }]
                          }, {
                            name: 'UV',
                            data: [{
                              color: '#ff6a00',
                              radius: '62%',
                              innerRadius: '38%',
                              y: ((dataWind[0] && dataWind[0].d09) / 11) * 100
                            }]
                          }]}
                          graphType="gauge"
                          title="RH, Solar and UV Radiation"
                          min={0}
                          max={100}
                          height="calc(23vh)"
                        />
                        <div style={{ textAlign: 'center', margin: '0px' }}>
                          {`HR: ${!isNaN(dataWind[0] && dataWind[0].d03) ? (dataWind[0] && dataWind[0].d03) : ''}% - R_s: ${!isNaN(dataWind[0] && dataWind[0].d06) ? (dataWind[0] && dataWind[0].d06.toFixed(2)) : ''}W/m2 - UV: ${!isNaN(dataWind[0] && dataWind[0].d09) ? (dataWind[0] && dataWind[0].d09.toFixed(2)) : ''}`}
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-12 col-md-2">
                      <div className="col">
                        { /* Accumulated Rain  */ }
                        <ChartSimple
                          data={[{ data: [dataAc[14] && dataAc[14][5]],
                            dataLabels: {
                              format:
                                '<div style="text-align:center;>'
                                + '<span style="font-size:17px">{y:,.2f} </span>'
                                + '<span style="font-size:8px">l/m2</span>'
                                + '</div>'
                            }, }]}
                          text-align="center"
                          graphType="gaugeSimple"
                          title="Accumulated Rain"
                          min={0}
                          max={100}
                        />
                        <div style={{ textAlign: 'center', margin: '0px' }}>
                          {`Rainfall Last 10 min: ${!isNaN(dataWind[0] && dataWind[0].d04) ? (((dataWind[0] && dataWind[0].d04.toFixed(2)) - (dataWind[10] && dataWind[10].d04.toFixed(2))).toFixed(2)) : ''} l/m2`}
                        </div>
                      </div>
                      <div className="col" onClick={this.onModalOpen} style={{ cursor: 'pointer' }}>
                        { /* Wind Rose  */ }
                        <ChartWindRose data={data5} title="Accumulated Wind-Dir" />
                        <div style={{ textAlign: 'center', margin: '0px' }}>
                          Click over to enlarge
                        </div>
                      </div>
                      <ContainerModal
                        fullScreenButton={false}
                        show={modalShow}
                        title="Accumulated Wind Direction"
                        modalClass="selector-modal-70"
                        onClose={this.onModalClose}
                        footerButtons={[
                          {
                            text: 'OK',
                            className: 'btn btn-primary',
                            onClick: this.onModalClose,
                          },
                        ]}
                      >
                        <ChartWindRose data={data5} title="Accumulated Wind Direction" />
                      </ContainerModal>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-sm-12 col-md-6" style={{ textAlign: 'center', height: '300px' }}>
                      <header>
                        <span className="widget-icon" style={{ padding: '8px' }}>
                          <i className="fa fa-edit" />
                        </span>
                        Daily Summary Data
                      </header>
                      <Table
                        data={(data2?.reverse()) || []}
                        alias={columnAlias2}
                        customCellStyles={columnStyles2}
                        hiddenCols={columnsToHide2}
                        buttons={this.buttons}
                        rowDataUpdated={this.rowDataUpdated}
                        customColumnsDefs={customColumnsDefs2}
                        height="calc(10vh - 1px)"
                        pagination
                      />
                    </div>
                    <div className="col-sm-12 col-md-6" style={{ textAlign: 'center', height: '300px' }}>
                      <header>
                        <span className="widget-icon" style={{ padding: '8px' }}>
                          <i className="fa fa-edit" />
                        </span>
                        Real Time Data
                      </header>
                      <Table
                        data={data1 || []}
                        alias={columnAlias1}
                        customCellStyles={columnStyles1}
                        hiddenCols={columnsToHide1}
                        buttons={this.buttons}
                        rowDataUpdated={this.rowDataUpdated}
                        customColumnsDefs={customColumnsDefs1}
                        height="calc(10vh - 1px)"
                        pagination
                        enableAdjustGridSize
                      />
                    </div>
                  </div>
                </div>
              </JarvisWidget>
            </article>
          </div>
        </WidgetGrid>
      </div>
    );
  }
}
